import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { PERMISSION_MODULES as PM } from '../constants/permissionModules';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import ReportDocument from '../components/devreport/presentation/ReportDocument';
import { buildPresentation, pdfFilename } from '../components/devreport/presentation/presentation';
import { ArrowLeft, Printer, Download, Lock, AlertTriangle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DevelopmentReportPreview = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { user, hasModuleAccess } = useAuth();
  const isAdmin = user?.role === 'Admin';
  const canFinalize = isAdmin || hasModuleAccess('development_reports.finalize', 'edit');
  const canExport = isAdmin || hasModuleAccess('development_reports.export', 'edit') || hasModuleAccess(PM.DEVELOPMENT_REPORTS, 'view');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFinalize, setShowFinalize] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/development-reports/${reportId}`);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
      navigate('/development-reports');
    } finally {
      setLoading(false);
    }
  }, [reportId, navigate]);

  useEffect(() => { load(); }, [load]);

  const finalize = async () => {
    setFinalizing(true);
    try {
      await axios.post(`${API}/development-reports/${reportId}/finalize`);
      toast.success('Report finalized');
      navigate(`/development-reports/${reportId}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg = 'Finalization failed.';
      if (e.response?.status === 403) msg = typeof detail === 'string' && detail.includes('unauthorised') ? 'Cannot finalize — the report includes sections you are not permitted to assemble.' : 'You do not have permission to finalize this report.';
      else if (typeof detail === 'string') msg = detail;
      toast.error(msg);
      setShowFinalize(false);
    } finally {
      setFinalizing(false);
    }
  };

  const openPrint = () => window.open(`/development-reports/${reportId}/print`, '_blank');

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/development-reports/${reportId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const p = buildPresentation(data);
      a.download = pdfFilename(p.report, p.overview);
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let detail = '';
      try { detail = JSON.parse(await e.response.data.text()).detail; } catch (_) { /* noop */ }
      console.warn('[PDF] one-click download failed:', detail || e.message);
      toast.error(detail ? `PDF failed: ${detail}` : 'One-click PDF failed — opening Print / Save as PDF instead.');
      openPrint();
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div className="py-16 text-center text-slate-400">Loading final document preview…</div>;
  if (!data) return null;
  const pres = buildPresentation(data);
  const isDraft = pres.isDraft;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="report-final-preview">
      {/* Toolbar */}
      <div className="no-print sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(isDraft ? `/development-reports/${reportId}/edit` : `/development-reports/${reportId}`)} className="text-slate-400 hover:text-slate-700" data-testid="preview-back">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <div className="text-sm font-semibold text-slate-800">Final Document Preview</div>
              <div className="text-xs text-slate-400">{isDraft ? 'Draft — this is exactly how the finalized report will look' : 'Finalized report'}</div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isDraft && (
              <Button variant="outline" size="sm" onClick={() => navigate(`/development-reports/${reportId}/edit`)} data-testid="preview-back-to-edit">
                Back to Edit
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={openPrint} data-testid="preview-print">
              <Printer className="mr-1.5 h-4 w-4" /> Print / Save as PDF
            </Button>
            {canExport && (
              <Button variant="outline" size="sm" onClick={downloadPdf} disabled={downloading} data-testid="preview-download-pdf">
                {downloading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Download PDF
              </Button>
            )}
            {isDraft && canFinalize && (
              <Button size="sm" onClick={() => setShowFinalize(true)} data-testid="preview-finalize">
                <Lock className="mr-1.5 h-4 w-4" /> Finalize &amp; Lock
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="py-6">
        <ReportDocument report={pres.report} sections={pres.sections} overview={pres.overview} lead={pres.lead} />
      </div>

      <Dialog open={showFinalize} onOpenChange={setShowFinalize}>
        <DialogContent data-testid="preview-finalize-dialog">
          <DialogHeader><DialogTitle>Finalize &amp; Lock Report</DialogTitle></DialogHeader>
          <div className="space-y-2 py-1 text-sm text-slate-600">
            <p>Finalizing freezes an immutable snapshot of:</p>
            <ul className="ml-4 list-disc space-y-0.5 text-[13px]">
              <li>Selected Hub evidence &amp; benchmark/reference snapshots</li>
              <li>Manual Staff Assessments &amp; Football Assessment Areas</li>
              <li>Strengths, Priorities &amp; the Development Action Plan</li>
              <li>PFAP Staff Targets &amp; Custom Sections</li>
            </ul>
            <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Once finalized the report is locked. Later changes to source modules will NOT rewrite this report.</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFinalize(false)} disabled={finalizing}>Cancel</Button>
            <Button onClick={finalize} disabled={finalizing} data-testid="preview-confirm-finalize">{finalizing ? 'Finalizing…' : 'Finalize & Lock'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DevelopmentReportPreview;
